/* eslint-disable react/self-closing-comp */
import React, { memo, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Send, SmilePlus } from 'lucide-react';
import ReactTextareaAutosize from 'react-textarea-autosize';
import { IFriend } from '../../types/friend';
import { SendMessagePayload } from '../../types/chat';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { useOnClickOutside } from 'usehooks-ts';
import { useDarkMode } from '../../hooks';

interface IChatEditorSchema {
  chatContent: string;
}

interface ChatEditorProps {
  user: IFriend;
  handleSendMessage: (payload: SendMessagePayload) => void;
}

const editorId = 'input-chat-editor';
const editorSubmitButton = 'button-submit-editor';

const chatEditorSchema = yup.object().shape({
  chatContent: yup.string().required(),
});

const ChatEditor = ({ user, handleSendMessage }: ChatEditorProps) => {
  const emojiRef = useRef<HTMLDivElement>(null);

  const { isDarkMode } = useDarkMode();

  const [visibleEmoji, setVisibleEmoji] = useState<boolean>(false);

  const { register, handleSubmit, setValue, setFocus, getValues, watch } =
    useForm<IChatEditorSchema>({
      mode: 'all',
      defaultValues: {
        chatContent: '',
      },
      resolver: yupResolver(chatEditorSchema),
    });

  const onSubmit: SubmitHandler<IChatEditorSchema> = (formData) => {
    try {
      const { chatContent } = formData;

      const payload: SendMessagePayload = {
        RecipientUserId: user.userId,
        Content: chatContent,
      };

      handleSendMessage(payload);
      setValue('chatContent', '');
      setFocus('chatContent');
    } catch (error) {
      console.log(error);
    }
  };

  const handleInsertEmoji = (emoji: EmojiClickData) => {
    const editorInput = document.getElementById(
      editorId
    ) as HTMLTextAreaElement;
    const cursor = editorInput?.selectionStart;

    const _value = getValues('chatContent');
    const insertedEmoji =
      _value.slice(0, cursor) + emoji.emoji + _value.slice(cursor);

    setValue('chatContent', insertedEmoji);
    setFocus('chatContent');
  };

  useEffect(() => {
    setFocus('chatContent');
  }, [user]);

  useOnClickOutside(emojiRef, () => {
    setVisibleEmoji(false);
  });
  const chatContent = watch('chatContent');
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex items-center md:gap-4 gap-4 md:p-3 p-2 overflow-hidden"
    >
      <div className="flex items-center h-full dark:text-white">
        <svg
          className=" cursor-pointer"
          onClick={() => setVisibleEmoji((prev) => !prev)}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 12C2 17.5228 6.47715 22 12 22C12.6477 22 13.2503 21.7004 13.7083 21.2424L21.2424 13.7083C21.7004 13.2503 22 12.6477 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12Z"
            stroke="#657384"
            strokeWidth="1.5"
          />
          <path
            d="M12 17C10.8846 17 9.85038 16.6303 9 16"
            stroke="#657384"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <ellipse cx="15" cy="10.5" rx="1" ry="1.5" fill="#657384" />
          <ellipse cx="9" cy="10.5" rx="1" ry="1.5" fill="#657384" />
          <path
            d="M12 22C12 19.2071 12 17.8107 12.3928 16.688C13.0964 14.6773 14.6773 13.0964 16.688 12.3928C17.8107 12 19.2071 12 22 12"
            stroke="#657384"
            strokeWidth="1.5"
          />
        </svg>
      </div>
      <div className="relative h-[54px] flex-1">
        <ReactTextareaAutosize
          style={{ height: 54 }}
          className="w-full resize-none h-[54px] text-[#18191E] dark:text-white placeholder:text-[#BEBEBE] bg-transparent outline-none  leading-[22px] text-[18px] border rounded-2xl border-[#DEE0E6] dark:border-slate-700 p-4"
          placeholder="Aaa..."
          {...register('chatContent')}
          id={editorId}
          onKeyDown={(e) => {
            if (e.code === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              const buttonSubmit = document.getElementById(editorSubmitButton);

              buttonSubmit?.click();
            }
          }}
        />
      </div>
      <div className="absolute right-4 bottom-16" ref={emojiRef}>
        <EmojiPicker
          open={visibleEmoji}
          onEmojiClick={(emoji) => {
            handleInsertEmoji(emoji);
          }}
          theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
        />
      </div>

      <button type="submit" id={editorSubmitButton}>
        <svg
          width="25"
          height="28"
          viewBox="0 0 25 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M25.0001 13.9863C25.001 14.3426 24.9067 14.6927 24.7269 15.0004C24.5472 15.3081 24.2885 15.5621 23.9776 15.7363L2.98883 27.7375C2.68727 27.9085 2.34674 27.9989 2.00008 28C1.68039 27.9994 1.36551 27.9221 1.08184 27.7747C0.798172 27.6272 0.553977 27.414 0.369727 27.1527C0.185476 26.8915 0.0665391 26.5898 0.0228847 26.2731C-0.0207697 25.9564 0.0121312 25.6339 0.118829 25.3325L3.53758 15.3388C3.57127 15.2399 3.63507 15.1541 3.72004 15.0933C3.80501 15.0325 3.90687 14.9999 4.01133 15H13.0001C13.1372 15.0003 13.2729 14.9724 13.3987 14.9181C13.5246 14.8637 13.6379 14.7841 13.7317 14.6841C13.8255 14.5841 13.8977 14.4659 13.9439 14.3368C13.99 14.2078 14.0092 14.0706 14.0001 13.9338C13.9774 13.6766 13.8584 13.4375 13.667 13.2644C13.4755 13.0913 13.2257 12.9968 12.9676 13H4.02008C3.91578 13.0002 3.81403 12.9678 3.72908 12.9073C3.64413 12.8467 3.58023 12.7612 3.54633 12.6625L0.116329 2.66253C-0.0172428 2.27934 -0.0310919 1.86459 0.0766214 1.47334C0.184335 1.0821 0.408514 0.732879 0.719389 0.472053C1.03026 0.211227 1.41313 0.051138 1.81713 0.0130445C2.22114 -0.0250489 2.62718 0.0606555 2.98133 0.258777L23.9813 12.245C24.2902 12.4189 24.5473 12.6718 24.7263 12.9778C24.9053 13.2838 24.9998 13.6318 25.0001 13.9863Z"
            fill={chatContent ? '#007AFF' : '#657384'}
          />
        </svg>
      </button>
    </form>
  );
};

export default memo(ChatEditor);
